import { defineStore } from 'pinia';
import { ref, computed, watch, nextTick } from 'vue';
import { useDebounceFn, watchOnce } from '@vueuse/core';
import { cloneDeep } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { getCvFile } from '../services/cvFile';
import * as logger from '../utils/logger';
// eslint-disable-next-line import/no-cycle
import { useProfile } from './profile-store';
// eslint-disable-next-line import/no-cycle
import { useAuth } from './auth-store';
// eslint-disable-next-line import/no-cycle
import { usePayloadStore } from './payload-store';
import { renderCv } from '../services/renderCv';
// eslint-disable-next-line import/no-cycle
import { useErrorStore } from './error-store';

const CV_RENDER_DEBOUNCE_TIME = 300; // ms

function sortAttachments(a, b) {
    const older = new Date(a.createDate).valueOf() > new Date(b.createDate).valueOf();
    return older ? 1 : -1;
}

export const CV_TYPES = {
    NONE: 'none',
    CUSTOM: 'custom',
    KARRIEREAT: 'karriere',
};

export const useApplicationStore = defineStore('application', () => {
    const profile = useProfile();
    const auth = useAuth();
    const payload = usePayloadStore();

    /* State */
    const firstname = ref('');
    const lastname = ref('');
    const salutation = ref('k');
    const email = ref('');
    const applicationLetter = ref(null);
    const recaptchaResponse = ref(null);
    const attachments = ref([]);
    const initialApplication = ref({
        firstname: '',
        lastname: '',
        attachmentsCount: 0,
        applicationLetter: null,
    });
    const selectedCvType = ref(CV_TYPES.NONE);
    const isKarriereAtCvSelected = computed(() => selectedCvType.value === CV_TYPES.KARRIEREAT);
    const isCustomCvSelected = computed(() => selectedCvType.value === CV_TYPES.CUSTOM);
    const isNoCvSelected = computed(() => selectedCvType.value === CV_TYPES.NONE);

    const selfUploadedCv = ref(null);
    const cvFile = ref({
        uuid: undefined,
        isCv: true,
        isDownloading: false,
        isRendering: false,
        name: '.pdf',
        size: 0,
        createDate: undefined,
        previewLink: undefined,
        progress: 25,
        hasRenderError: false,
    });
    const hasSeenPreview = ref(false);
    const uuid = ref(null);
    const applicationUuid = ref(uuidv4());

    /* Getters */
    const isDirty = computed(() => firstname.value !== initialApplication.value.firstname ||
            lastname.value !== initialApplication.value.lastname ||
            attachments.value.length !== initialApplication.value.attachmentsCount ||
            applicationLetter.value !== initialApplication.value.applicationLetter);
    const showApplicationLetter = computed(() => applicationLetter.value !== null);
    const activeAttachments = computed(() => cloneDeep(attachments.value)
        .filter(({ shouldBeAttachedToApplication }) => shouldBeAttachedToApplication).sort(sortAttachments));
    const hasActiveAttachments = computed(() => Boolean(activeAttachments.value.length));
    const anyProfileDocumentIsInActiveAttachments = computed(
        () => activeAttachments.value.some(attachment => attachment.isProfileDocument),
    );
    const anyUploadedDocumentIsInActiveAttachments = computed(
        () => activeAttachments.value.some(attachment => !attachment.isProfileDocument),
    );

    const cvRelevantData = ref({
        firstname: firstname.value,
        surname: lastname.value,
        email: email.value,
    });

    // Use function over computed for better control on when to send a render call,
    // mostly to still allow eager model and therefore vuelidate validation while not sending too many render calls
    function updateCvRenderData() {
        cvRelevantData.value = {
            firstname: firstname.value,
            surname: lastname.value,
            email: email.value,
        };
    }

    const canRenderCvFromData = computed(() => {
        if (!firstname.value || !lastname.value || !email.value) return false;

        return true;
    });

    const inactiveAttachments = computed(
        () => cloneDeep(attachments.value).filter(
            ({ shouldBeAttachedToApplication }) => !shouldBeAttachedToApplication,
        ).sort(sortAttachments),
    );
    const isMailDifferentFromAccount = computed(() => auth.isLoggedIn && email.value !== auth.email);
    const isFirstnameDifferentFromAccount = computed(() => auth.isLoggedIn && firstname.value !== profile.firstname);
    const isLastnameDifferentFromAccount = computed(() => auth.isLoggedIn && lastname.value !== profile.lastname);
    const isSalutationDifferentFromAccount = computed(() => auth.isLoggedIn && salutation.value !== profile.firstname);
    const selectedCv = computed(() => {
        if (selectedCvType.value === CV_TYPES.KARRIEREAT) return cvFile.value;
        if (selectedCvType.value === CV_TYPES.CUSTOM) return selfUploadedCv.value;
        return null;
    });
    const serializedAttachments = computed(() => {
        const serializedFiles = activeAttachments.value.map(({ uuid: attachmentId, isProfileDocument }) => ({
            type: 'attachment',
            origin: isProfileDocument ? 'profile' : 'upload',
            uuid: attachmentId,
        }));
        if (selectedCvType.value === CV_TYPES.KARRIEREAT) {
            serializedFiles.unshift({
                type: 'cv',
                origin: 'profile',
                uuid: cvFile.value.uuid,
            });
        } else if (selectedCvType.value === CV_TYPES.CUSTOM) {
            serializedFiles.unshift({
                type: 'cv',
                origin: 'upload',
                uuid: selfUploadedCv.value.uuid,
            });
        }
        return serializedFiles;
    });
    const isCvRendering = computed(() => cvFile.value.isRendering);
    const hasCvRenderError = computed(() => cvFile.value.hasRenderError);

    const renderCvAction = useDebounceFn(async () => {
        const errorState = useErrorStore();
        errorState.cvLoadingError = false;

        cvFile.value = {
            ...cvFile.value,
            isRendering: true,
            hasRenderError: false,
        };

        const requestBody = {
            jobAdId: payload.jobId,
            jobAdTitle: payload.jobTitle,
            atsUserUuid: payload.atsUserUuid,
            uuid: applicationUuid.value,
            ...cvRelevantData.value,
        };

        try {
            const { data } = await renderCv(requestBody);

            cvFile.value = {
                ...cvFile.value,
                uuid: data.id,
                isCv: true,
                hasRenderError: false,
                name: data.name,
                size: data.size,
                createDate: data.createDate,
                previewLink: data.previewLink,
                progress: undefined,
            };
        } catch (e) {
            cvFile.value.hasRenderError = true;
        } finally {
            cvFile.value.isRendering = false;
        }
    }, CV_RENDER_DEBOUNCE_TIME);

    async function fetchCvAction() {
        if (!profile.hasProfile) return;

        cvFile.value.hasRenderError = false;

        const errorState = useErrorStore();

        cvFile.value = {
            ...cvFile.value,
            isDownloading: true,
            progress: 25,
        };

        try {
            const { data } = await getCvFile();

            // The endpoint cv-file returns data: "null" if there went something wrong.
            // It always returns 200 Ok.
            if (data === null) {
                errorState.setCvError();
                return;
            }

            cvFile.value = {
                ...cvFile.value,
                uuid: data.id,
                isCv: true,
                name: data.name,
                size: data.size,
                createDate: data.createDate,
                previewLink: data.previewLink,
                progress: undefined,
            };
        } catch (exception) {
            // Catch response of 500.
            errorState.setCvError();
            logger.error({
                exception,
                message: 'Fetching cv file failed.',
            });
        } finally {
            cvFile.value.isDownloading = false;
        }
    }

    function addAttachment(attachment) {
        // eslint-disable-next-line no-param-reassign
        attachment.shouldBeAttachedToApplication = true;
        attachments.value.push(attachment);
    }

    function set(application) {
        email.value = application.basics.contact.email;

        if (application.flags.exists) {
            firstname.value = application.basics.firstname || '';
            lastname.value = application.basics.surname || '';
            salutation.value = application.basics.salutation ?? 'k';

            application.attachments.forEach((attachment) => {
                if (attachments.value.find(({ uuid: attachmentId }) => attachmentId === attachment.uuid)) return;
                // eslint-disable-next-line no-param-reassign
                attachment.isProfileDocument = true;
                addAttachment(attachment);
            });
        }

        initialApplication.value = {
            firstname: firstname.value,
            lastname: lastname.value,
            attachmentsCount: attachments.value.length || 0,
            applicationLetter: applicationLetter.value,
        };

        updateCvRenderData();
    }

    function updateCheckedAttachment({ attachmentUuid, isChecked }) {
        const attachment = attachments.value.find(({ uuid: attachmentId }) => attachmentId === attachmentUuid);
        attachment.shouldBeAttachedToApplication = isChecked;
    }

    function activateAttachment(attachmentUuid) {
        updateCheckedAttachment({
            attachmentUuid,
            isChecked: true,
        });
    }

    function deactivateAttachment(attachmentUuid) {
        updateCheckedAttachment({
            attachmentUuid,
            isChecked: false,
        });
    }

    function removeApplicationLetter() {
        applicationLetter.value = null;
    }

    function addApplicationLetter() {
        applicationLetter.value = '';
    }

    function updateUuid(newUuid) {
        uuid.value = newUuid;
    }

    let unwatchCvRender = () => {};
    let unwatchProfileFetch = () => {};
    function initializeCvRenderWatcherForUsersWithProfile() {
        unwatchProfileFetch();
        unwatchCvRender();

        unwatchProfileFetch = watchOnce(() => profile.profileFetched, async () => {
            if (!profile.hasProfile) return;

            // wait for fields to auto-fill before starting watcher to avoid unnecessary render call
            await nextTick();

            unwatchCvRender = watch(cvRelevantData, (newValue, oldValue) => {
                if (!canRenderCvFromData.value) return;
                if (JSON.stringify(oldValue) === JSON.stringify(newValue)) return;

                renderCvAction();
            });
        });
    }
    initializeCvRenderWatcherForUsersWithProfile();

    function $reset() {
        initializeCvRenderWatcherForUsersWithProfile();

        firstname.value = '';
        lastname.value = '';
        salutation.value = 'k';
        email.value = '';
        applicationLetter.value = null;
        recaptchaResponse.value = null;
        attachments.value = [];
        initialApplication.value = {
            firstname: '',
            lastname: '',
            attachmentsCount: 0,
            applicationLetter: null,
        };
        selectedCvType.value = CV_TYPES.NONE;
        selfUploadedCv.value = null;
        cvFile.value = {
            isCv: true,
            isDownloading: false,
            isRendering: false,
            name: '.pdf',
            size: 0,
            createDate: undefined,
            previewLink: undefined,
            progress: 25,
            hasRenderError: false,
        };
        hasSeenPreview.value = false;
        uuid.value = null;
        applicationUuid.value = uuidv4();
    }

    return {
        firstname,
        lastname,
        salutation,
        email,
        applicationLetter,
        recaptchaResponse,
        attachments,
        initialApplication,
        selectedCvType,
        selfUploadedCv,
        cvFile,
        hasSeenPreview,
        uuid,
        applicationUuid,

        isDirty,
        showApplicationLetter,
        activeAttachments,
        hasActiveAttachments,
        anyProfileDocumentIsInActiveAttachments,
        anyUploadedDocumentIsInActiveAttachments,
        inactiveAttachments,
        isMailDifferentFromAccount,
        isFirstnameDifferentFromAccount,
        isLastnameDifferentFromAccount,
        isSalutationDifferentFromAccount,
        selectedCv,
        serializedAttachments,
        isCvRendering,
        hasCvRenderError,
        isKarriereAtCvSelected,
        isCustomCvSelected,
        isNoCvSelected,

        updateCvRenderData,
        renderCv: renderCvAction,
        fetchCv: fetchCvAction,
        set,
        addAttachment,
        updateCheckedAttachment,
        activateAttachment,
        deactivateAttachment,
        removeApplicationLetter,
        addApplicationLetter,
        updateUuid,
        $reset,
    };
}, {
    persist: {
        storage: sessionStorage,
        afterHydrate: ({ store }) => {
            // Do not cache render errors
            // eslint-disable-next-line no-param-reassign
            store.cvFile.hasRenderError = false;
        },
    },
});
